"use client";

import type { AcceptanceDocument } from "@fm/api";
import { useState } from "react";
import {
  SandboxedDocumentFrame,
  type DocumentCompletenessStatus,
  type DocumentFormValues,
} from "@fm/ui";

const INCOMPLETE_HINT =
  "Complete the required fields and signature to continue";

export interface AcceptanceDocumentsSectionProps {
  documents: AcceptanceDocument[];
  acceptedVersionIds: ReadonlySet<string>;
  onChange: (next: Set<string>) => void;
  /** What the customer typed or drew inside a document, per version id. */
  onFormData?: (versionId: string, values: DocumentFormValues) => void;
  /** A document was shown to the customer (its frame finished loading). */
  onViewed?: (versionId: string) => void;
  disabled?: boolean;
  isLoading?: boolean;
  className?: string;
}

/**
 * fleet-agreement-acceptance (path 1, embedded widget checkout): renders the
 * vehicle's mandatory documents as real, unchecked-by-default checkboxes.
 * The customer must read and explicitly check each one before checkout can
 * continue — this must hold regardless of the server's
 * `fleet_agreement_acceptance_enforced` flag, which only controls whether the
 * server REJECTS a hold missing acceptance, not whether the widget asks.
 */
export function AcceptanceDocumentsSection({
  documents,
  acceptedVersionIds,
  onChange,
  onFormData,
  onViewed,
  disabled = false,
  isLoading = false,
  className = "",
}: AcceptanceDocumentsSectionProps) {
  // Per version id. No entry means no status arrived yet: fail open.
  const [statuses, setStatuses] = useState<
    Record<string, DocumentCompletenessStatus>
  >({});

  if (isLoading || documents.length === 0) return null;

  const handleStatus = (
    versionId: string,
    status: DocumentCompletenessStatus,
  ) => {
    setStatuses((current) => ({ ...current, [versionId]: status }));
    if (!status.complete && acceptedVersionIds.has(versionId)) {
      const next = new Set(acceptedVersionIds);
      next.delete(versionId);
      onChange(next);
    }
  };

  const toggle = (versionId: string, checked: boolean) => {
    const next = new Set(acceptedVersionIds);
    if (checked) {
      next.add(versionId);
    } else {
      next.delete(versionId);
    }
    onChange(next);
  };

  return (
    <div
      className={`space-y-4 ${className}`}
      data-testid="acceptance-documents-section"
    >
      <p className="text-sm text-gray-600">
        Complete and sign each document before checking its box. Required fields
        are enforced.
      </p>
      {documents.map((document) => {
        const inputId = `acceptance-document-${document.document_version_id}`;
        const checked = acceptedVersionIds.has(document.document_version_id);
        const status = statuses[document.document_version_id];
        const incomplete = status !== undefined && !status.complete;
        const hintId = `${inputId}-hint`;

        return (
          <div
            key={document.document_version_id}
            className="rounded-lg border border-gray-200 bg-white p-4"
          >
            <div className="mb-2 flex flex-wrap items-center gap-2">
              <h3 className="text-sm font-semibold text-gray-900">
                {document.name}
              </h3>
              {status && !status.complete && (
                <span
                  role="status"
                  className="rounded bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-800"
                >
                  {`${status.missing} required ${status.missing === 1 ? "field" : "fields"} missing`}
                </span>
              )}
              {status?.complete && (
                <span className="rounded bg-green-50 px-2 py-0.5 text-xs font-medium text-green-800">
                  Complete
                </span>
              )}
            </div>
            <SandboxedDocumentFrame
              title={document.name}
              content={document.content}
              className="h-72"
              onFormData={(values) =>
                onFormData?.(document.document_version_id, values)
              }
              onViewed={() => onViewed?.(document.document_version_id)}
              onStatus={(next) =>
                handleStatus(document.document_version_id, next)
              }
            />
            <label
              htmlFor={inputId}
              className="mt-3 flex items-start gap-3 cursor-pointer"
            >
              <input
                type="checkbox"
                id={inputId}
                checked={checked}
                disabled={disabled || incomplete}
                title={incomplete ? INCOMPLETE_HINT : undefined}
                aria-describedby={incomplete ? hintId : undefined}
                onChange={(e) =>
                  toggle(document.document_version_id, e.target.checked)
                }
                className="mt-1 h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary disabled:opacity-50"
              />
              <span className="text-sm">
                {`I have read and agree to ${document.name}.`}
              </span>
              {incomplete && (
                <span id={hintId} className="sr-only">
                  {INCOMPLETE_HINT}
                </span>
              )}
            </label>
          </div>
        );
      })}
    </div>
  );
}
